// src/components/dashboard/ChartPropertiesModal.tsx
import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import http from "services/httpClient";

interface Label {
  label: string;
  column_index: number;
  unit?: string;
  is_enabled: boolean;
  kind?: "raw" | "calc" | "stat";
}

interface StatDefinition {
  id: number;
  source_column_index: number;
  aggregation: "min" | "max" | "avg";
  window_hours: 1 | 6 | 8 | 12 | 24;
  result_label: string;
  unit?: string | null;
}

interface ChartPropertiesModalProps {
  show: boolean;
  onClose: () => void;
  config: any;
  deviceId: number;
  refetchGraphs: () => void;
}

export default function ChartPropertiesModal({
  show,
  onClose,
  config,
  deviceId,
  refetchGraphs,
}: ChartPropertiesModalProps) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | "">("");
  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);
  const [labelColors, setLabelColors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!show) return;

    const loadLabels = async () => {
      try {
        const [rawRes, calcRes, statRes] = await Promise.all([
          http.get(`/device/${deviceId}/labels`),
          http.get(`/calculated_params/${deviceId}`),
          http.get(`/stat-definitions/device/${deviceId}`),
        ]);

        const rawLabels: Label[] = (rawRes.data || []).map((r: any) => ({
          column_index: r.column_index,
          label: r.label,
          unit: r.unit,
          is_enabled: !!r.is_enabled,
          kind: "raw",
        }));

        const calcLabels: Label[] = (calcRes.data || []).map((c: any) => ({
          column_index: c.column_index,
          label: c.result_label,
          unit: c.unit,
          is_enabled: true,
          kind: "calc",
        }));

        const statLabels: Label[] = (statRes.data || []).map((s: StatDefinition) => ({
          column_index: 1000 + s.id,
          label: s.result_label,
          unit: s.unit || "",
          is_enabled: true,
          kind: "stat",
        }));

        const combinedLabels: Label[] = [...rawLabels, ...calcLabels, ...statLabels];
        setLabels(combinedLabels);

        if (Array.isArray(config.chart_labels)) {
          const selected: Label[] = config.chart_labels.map((item: any) => {
            const colIndex = typeof item === "object" ? item.column_index : item;
            const match = combinedLabels.find((l) => l.column_index === colIndex);

            const fallbackLabel: Label = {
              column_index: colIndex,
              label:
                colIndex >= 1000
                  ? `Stat ${colIndex}`
                  : colIndex >= 100
                  ? `Calc ${colIndex}`
                  : `Col ${colIndex + 1}`,
              is_enabled: true,
              unit: "",
              kind: colIndex >= 1000 ? "stat" : colIndex >= 100 ? "calc" : "raw",
            };

            return match ?? fallbackLabel;
          });

          const colorMap: Record<number, string> = {};
          config.chart_labels.forEach((item: any) => {
            const colIndex = typeof item === "object" ? item.column_index : item;
            const color = typeof item === "object" ? item.color : undefined;
            colorMap[colIndex] = color || "#ffffff";
          });

          setSelectedLabels(selected);
          setLabelColors(colorMap);
        } else {
          setSelectedLabels([]);
          setLabelColors({});
        }
      } catch (err) {
        // console.error("❌ Failed to load raw + calc + stat labels", err);
      }
    };

    loadLabels();
  }, [show, deviceId, config.chart_labels]);

  const handleAdd = () => {
    const label = labels.find((l) => l.column_index === selectedIndex);
    if (label && !selectedLabels.find((l) => l.column_index === label.column_index)) {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  const handleDelete = (index: number) => {
    const updated = [...selectedLabels];
    updated.splice(index, 1);
    setSelectedLabels(updated);
  };

  const handleSave = async () => {
    const updatedConfig = {
      ...config,
      chart_labels: selectedLabels.map((l) => ({
        column_index: l.column_index,
        color: labelColors[l.column_index] || "#ffffff",
      })),
    };

    await http.patch(`/graphs/${config.id}`, {
      graph_config: updatedConfig,
    });

    refetchGraphs();
    onClose();
  };

  const renderLabelText = (l: Label) => {
    const suffix =
      l.kind === "stat"
        ? " [stat]"
        : l.kind === "calc"
        ? " [calc]"
        : !l.is_enabled
        ? " [Disabled]"
        : "";

    return `${l.label} [x${l.column_index}]${suffix}`;
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Chart Properties</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="d-flex gap-2">
          <Form.Select
            value={selectedIndex}
            onChange={(e) =>
              setSelectedIndex(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <option value="">-- Select Label --</option>
            {labels.map((l) => (
              <option
                key={l.column_index}
                value={l.column_index}
                disabled={!l.is_enabled}
              >
                {renderLabelText(l)}
              </option>
            ))}
          </Form.Select>

          <Button variant="primary" onClick={handleAdd} disabled={selectedIndex === ""}>
            Add
          </Button>
        </Form.Group>

        <hr />

        <div className="px-3 pb-3">
          <div className="d-flex flex-column gap-2">
            {selectedLabels.map((label, idx) => (
              <div
                key={label.column_index}
                className="d-flex align-items-center justify-content-between"
              >
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="color"
                    value={labelColors[label.column_index] || "#ffffff"}
                    onChange={(e) =>
                      setLabelColors((prev) => ({
                        ...prev,
                        [label.column_index]: e.target.value,
                      }))
                    }
                    style={{
                      width: "40px",
                      height: "30px",
                      padding: 0,
                      border: "1px solid #ccc",
                    }}
                  />

                  <span style={{ fontSize: "0.9rem" }}>
                    {label.label}
                    {label.kind === "calc" && (
                      <span
                        className="badge ms-2"
                        style={{
                          backgroundColor: "#cfe8ff",
                          color: "#0c5460",
                          fontWeight: 500,
                        }}
                      >
                        calc
                      </span>
                    )}
                    {label.kind === "stat" && (
                      <span
                        className="badge ms-2"
                        style={{
                          backgroundColor: "#ffe5b4",
                          color: "#7a4b00",
                          fontWeight: 500,
                        }}
                      >
                        stat
                      </span>
                    )}
                  </span>
                </div>

                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(idx)}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}